<template>
    <div>
        <b-container fluid>
            <div class="my-3 mt-5">
                <div class="d-flex align-items-center justify-content-between">
                    <div class="h3 m-0 mr-3 ml-3">
                        Weeks
                    </div>
                </div>
            </div>

            <b-form @submit.prevent="searchWeeks">
                <div class="d-flex align-items-center mb-3">
                    <div class="flex-grow-1">
                        <b-input-group>
                            <b-form-input
                                v-model="search_term"
                                type="search"
                                placeholder="Search Weeks"
                                :disabled="!can_search_weeks"
                            />
                            <b-input-group-append>
                                <b-btn
                                    variant="outline-secondary"
                                    :disabled="loading_weeks || !can_search_weeks"
                                    @click="loadLatestWeeks"
                                >
                                    Latest
                                </b-btn>
                                <b-btn
                                    type="submit"
                                    variant="outline-secondary"
                                    :disabled="loading_weeks || !can_search_weeks"
                                >
                                    <font-awesome-icon :icon="['fad', 'search']" />
                                </b-btn>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                    <div class="ml-2">
                        <b-btn
                            id="weeks-search-info"
                            variant="light"
                            :disabled="!can_search_weeks"
                        >
                            <font-awesome-icon :icon="['fad', 'info-circle']" />
                        </b-btn>
                        <b-tooltip
                            target="weeks-search-info"
                            triggers="hover focus"
                            placement="bottom"
                        >
                            Search by week ID, title, or description. Use AND, OR, NOT, and quotes for exact
                            phrases. Use * for prefix wildcard.
                        </b-tooltip>
                    </div>
                    <div class="d-flex">
                        <div class="mx-2">
                            <b-btn
                                variant="light"
                                :class="filters_visible ? 'active' : 'collapsed'"
                                :disabled="!can_search_weeks"
                                @click="filters_visible = !filters_visible"
                            >
                                <font-awesome-icon :icon="['fad', 'filter']" />
                            </b-btn>
                        </div>
                    </div>
                </div>
            </b-form>

            <b-collapse
                id="weeks-filters"
                v-model="filters_visible"
            >
                <div v-if="filters_visible" class="mb-3">
                    <div class="d-flex align-items-center">
                        <div class="flex-grow-1">
                            <div class="row align-items-center">
                                <div class="col-3 mt-lg-0 mt-1">
                                    <multiselect
                                        v-model="publication_type_filter"
                                        :options="publication_type_filter_options"
                                        label="text"
                                        track-by="value"
                                        :allow-empty="true"
                                        :show-labels="false"
                                        placeholder="Filter By Trunk/Branch"
                                    />
                                </div>
                            </div>
                        </div>
                        <div v-if="active_filters" class="ml-3">
                            <b-btn variant="danger" @click="clearFilters">
                                <font-awesome-icon :icon="['fad', 'backspace']" />
                                Clear All Filters
                            </b-btn>
                        </div>
                    </div>
                </div>
            </b-collapse>

            <b-alert
                v-if="bannerText"
                :variant="bannerVariant"
                show
                class="mb-3"
            >
                {{ bannerText }}
            </b-alert>

            <div v-if="loading_weeks" class="mb-3">
                <b-spinner label="Loading..."></b-spinner>
            </div>

            <b-table
                v-else
                :items="pagedWeeks"
                :fields="fields"
                :sort-compare="sortCompare"
                striped
                responsive
                sticky-header
                outlined
                no-border-collapse
                head-variant="light"
                empty-text="No weeks found."
            >
                <template #cell(id)="data">
                    {{ data.item.id }}
                </template>

                <template #cell(title)="data">
                    <a
                        v-if="data.item.publication_id"
                        class="h5"
                        href="#"
                        v-b-tooltip.hover
                        title="View additional week data"
                        @click.prevent="openWeekPreview(data.item)"
                    >
                        {{ data.item.title }}
                    </a>
                </template>

                <template #cell(publication)="data">
                    <div v-if="data.item.publication_id" class="h5">
                        {{ data.item.publication_sku }}
                    </div>
                    <div v-if="data.item.publication_sku" class="bold">
                            {{ data.item.publication_title }}
                        </div>
                </template>

                <template #cell(open_in_publication)="data">
                    <router-link
                        v-if="data.item.publication_id"
                        :to="publicationWeekRoute(data.item)"
                        class="btn btn-outline-primary btn-sm"
                        target="_blank"
                    >
                        Open In Publication
                    </router-link>
                </template>
            </b-table>

            <div v-if="!loading_weeks && filteredWeeks.length" class="d-flex align-items-center justify-content-between mt-3">
                <div class="d-flex align-items-center">
                    <span class="mr-2">Rows per page</span>
                    <b-form-select
                        v-model="per_page"
                        :options="per_page_options"
                        size="sm"
                        style="width: 90px;"
                    />
                </div>

                <b-pagination
                    v-model="current_page"
                    :per-page="per_page"
                    :total-rows="filteredWeeks.length"
                    align="right"
                    size="sm"
                    class="mb-0"
                />
            </div>

            <week-preview-modal
                v-model="show_week_preview_modal"
                :publication-id="selected_week_publication_id"
                :unit-id="selected_week_id"
            />
        </b-container>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import WeekPreviewModal from '@/components/modals/unit/WeekPreviewModal'

export default {
    name: 'WeeksView',
    components: {
        WeekPreviewModal,
    },
    data: () => ({
        loading_weeks: false,
        weeks: [],
        search_term: '',
        active_list: 'latest',
        show_week_preview_modal: false,
        selected_week_id: null,
        selected_week_publication_id: null,
        filters_visible: false,
        publication_type_filter: null,
        publication_type_filter_options: [
            { value: 'trunk', text: 'Trunk' },
            { value: 'branch', text: 'Branch' },
        ],
        current_page: 1,
        per_page: 25,
        per_page_options: [10, 25, 50, 100, 150],
        fields: [
            { key: 'id', label: 'ID', sortable: true, thStyle: { width: '5%' } },
            { key: 'title', label: 'Week Title', sortable: true, thStyle: { width: '45%' } },
            { key: 'publication', label: 'Publication', sortable: true, thStyle: { width: '45%' } },
            { key: 'open_in_publication', label: '', thStyle: { width: '5%' }, tdClass: 'text-nowrap' },
        ],
    }),
    computed: {
        ...mapState({
            user: state => state.user.user,
        }),
        can_search_weeks() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'search_week')
        },
        active_filters() {
            return !!this.publication_type_filter
        },
        filteredWeeks() {
            if (!this.weeks || !this.weeks.length) {
                return []
            }

            if (!this.publication_type_filter) {
                return this.weeks
            }

            return this.weeks.filter(week => {
                const trunkValue = week.publication_is_trunk
                const isTrunk = trunkValue === true || trunkValue === 1 || trunkValue === '1' || trunkValue === 'true'

                if (this.publication_type_filter.value === 'trunk') {
                    return isTrunk
                }

                if (this.publication_type_filter.value === 'branch') {
                    return !isTrunk
                }

                return true
            })
        },
        pagedWeeks() {
            if (!this.filteredWeeks.length) {
                return []
            }

            const start = (this.current_page - 1) * this.per_page
            const end = start + this.per_page

            return this.filteredWeeks.slice(start, end)
        },
        bannerText() {
            if (this.active_list === 'searched' && this.search_term) {
                return `Search results for "${this.search_term}"`
            }

            if (this.active_list === 'latest') {
                return 'Showing latest weeks'
            }

            return ''
        },
        bannerVariant() {
            return this.active_list === 'searched' ? 'primary' : 'info'
        },
    },
    watch: {
        per_page() {
            this.current_page = 1
        },
        publication_type_filter() {
            this.current_page = 1
        },
        weeks(weeks) {
            const totalPages = Math.max(1, Math.ceil((weeks || []).length / this.per_page))

            if (this.current_page > totalPages) {
                this.current_page = 1
            }
        },
        filteredWeeks(weeks) {
            const totalPages = Math.max(1, Math.ceil((weeks || []).length / this.per_page))

            if (this.current_page > totalPages) {
                this.current_page = 1
            }
        },
    },
    methods: {
        clearFilters() {
            this.publication_type_filter = null
        },
        sortCompare(a, b, key) {
            if (key === 'publication') {
                return String(a.publication_sku || '').localeCompare(String(b.publication_sku || ''))
            }

            return false
        },
        publicationWeekRoute(week) {
            return {
                name: 'Publication',
                params: {
                    publication_id: week.publication_id,
                },
                query: {
                    unit_id: week.id,
                },
            }
        },
        openWeekPreview(week) {
            this.selected_week_id = week.id
            this.selected_week_publication_id = week.publication_id
            this.show_week_preview_modal = true
        },
        async searchWeeks() {
            this.active_list = 'searched'
            this.loading_weeks = true
            this.current_page = 1

            try {
                const sanitizedSearch = encodeURIComponent(this.search_term)
                const response = await this.axios.get(`/api/units/search/${sanitizedSearch}`, {
                    params: {
                        per_page: 150,
                    },
                })

                if (response.data) {
                    this.weeks = response.data
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to search weeks: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.loading_weeks = false
            }
        },
        async loadLatestWeeks() {
            this.active_list = 'latest'
            this.loading_weeks = true
            this.current_page = 1

            try {
                const response = await this.axios.get('/api/units/latest', {
                    params: {
                        per_page: 150,
                    },
                })

                if (response.data) {
                    this.weeks = response.data
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to get latest weeks: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.loading_weeks = false
            }
        },
    },
    mounted() {
        this.loadLatestWeeks()
    }
}
</script>
